import { InsuranceProgramMapping } from "DataTypes/Services/Insurance.type";
import { FormatOrgListType } from "DataTypes/PracticeMangement.type";
import { PracticeInsuranceRow } from "./types";
import { PrgmListResType } from "DataTypes/Services/Prgm.type";

export const getKey: (
  insurance: PracticeInsuranceRow,
  program: PrgmListResType
) => string = (insurance, program) => {
  return `${insurance.companyId}_${insurance.planId}_${program.id}`;
};

export const insuranceName: (insurance: PracticeInsuranceRow) => string = (
  insurance
) => {
  const name = [insurance.companyName, insurance.planName]
    .filter(Boolean)
    .join(" ");
  return name || "N/A";
};

export const getPracticeInsuranceCount: (
  practice: FormatOrgListType,
  selectedPractice: FormatOrgListType | null,
  insuranceCount: number
) => number = (practice, selectedPractice, insuranceCount) => {
  if (selectedPractice?.id == practice.id) return insuranceCount;
  return practice.insuranceCount || 0;
};

export const mergeProgramKeys: (
  current: Set<string>,
  programId: number,
  programKeys: string[]
) => Set<string> = (current, programId, programKeys) => {
  const nextKeys = removeProgramKeys(current, programId);
  programKeys.forEach((key) => nextKeys.add(key));
  return nextKeys;
};

export const removeProgramKeys: (
  keys: Set<string>,
  programId: number
) => Set<string> = (keys, programId) => {
  const nextKeys = new Set<string>();
  keys.forEach((key) => {
    if (!key.endsWith(`_${programId}`)) nextKeys.add(key);
  });
  return nextKeys;
};

export const syncSavedInitialKeys: (
  checkedKeys: Set<string>,
  initialCheckedKeys: Set<string>,
  savedProgramIds: Set<number>
) => Set<string> = (checkedKeys, initialCheckedKeys, savedProgramIds) => {
  let nextKeys = new Set(initialCheckedKeys);
  savedProgramIds.forEach((programId) => {
    nextKeys = removeProgramKeys(nextKeys, programId);
    checkedKeys.forEach((key) => {
      if (key.endsWith(`_${programId}`)) nextKeys.add(key);
    });
  });
  return nextKeys;
};

export const normalizeInsuranceRows: (
  data: any,
  practiceId: number
) => PracticeInsuranceRow[] = (data, practiceId) => {
  const rawRows = Array.isArray(data?.insurance)
    ? data.insurance
    : Array.isArray(data)
      ? data
      : [];
  return rawRows.map((el: any) => {
    const companyId = String(el?.companyId || "");
    const planId = String(el?.planId || "");
    return {
      id: `${companyId}_${planId}`,
      practiceId: Number(el?.practiceId || practiceId),
      companyId,
      companyName: String(el?.companyName || ""),
      planId,
      planName: String(el?.planName || ""),
    };
  });
};

export const normalizeMappings: (
  data: any,
  practiceId: number,
  programId: number
) => InsuranceProgramMapping[] = (data, practiceId, programId) => {
  const rawRows = Array.isArray(data?.insurance)
    ? data.insurance
    : Array.isArray(data)
      ? data
      : [];
  return rawRows.map((el: any) => ({
    practiceId: Number(el?.practiceId || practiceId),
    programId: Number(el?.programId || programId),
    companyId: String(el?.companyId || ""),
    planId: String(el?.planId || ""),
  }));
};

export const buildSavePayloads: (
  keys: Set<string>,
  insuranceRows: PracticeInsuranceRow[],
  programColumns: PrgmListResType[],
  practiceId: number,
  dirtyProgramIds: Set<number>
) => Array<{
  practiceId: number;
  programId: number;
  insurance: Array<Pick<InsuranceProgramMapping, "companyId" | "planId">>;
}> = (
  keys,
  insuranceRows,
  programColumns,
  practiceId,
  dirtyProgramIds
) => {
    return programColumns
      .filter((program) => program.id && dirtyProgramIds.has(Number(program.id)))
      .map((program) => ({
        practiceId,
        programId: Number(program.id),
        insurance: insuranceRows
          .filter((insurance) => keys.has(getKey(insurance, program)))
          .map((insurance) => ({
            companyId: insurance.companyId,
            planId: insurance.planId,
          })),
      }));
  };
